/**
 * Hook for uploading files for WhatsApp media messages
 * Provides a simple interface for uploading files that will be used in WhatsApp media messages
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessagingWhatsappMessagesService } from 'src/api/generated';
import type {
  WhatsAppMediaUploadBodyParams,
  WhatsAppMediaUploadResponse,
} from 'src/api/generated';
import { uploadFileWithUploadSession } from '../../files-upload-session';

/**
 * Parameters for WhatsApp media file upload
 */
export interface WhatsAppMediaUploadParams {
  file: File;
  fromPhoneNumber: string;
  tags?: Record<string, string>;
}

interface UseFileUploadMutationResponse {
  uploadFile: (
    params: WhatsAppMediaUploadParams
  ) => Promise<WhatsAppMediaUploadResponse>;
  loading: boolean;
  error: Error | null;
}

const handleDefaultSuccess = (): void => undefined;
const handleDefaultError = (): void => undefined;

export const useWhatsAppMediaUploadMutation = (
  onSuccess: (data: WhatsAppMediaUploadResponse) => void = handleDefaultSuccess,
  onError: (error: Error) => void = handleDefaultError
): UseFileUploadMutationResponse => {
  const queryClient = useQueryClient();

  const uploadFile = async (
    params: WhatsAppMediaUploadParams
  ): Promise<WhatsAppMediaUploadResponse> => {
    const { file, fromPhoneNumber, tags } = params;

    if (file.size === 0) {
      throw new Error('File is empty');
    }

    // Max file size check (100MB - largest allowed by WhatsApp)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      throw new Error('File is too large (max 100MB)');
    }

    const finalizedFile = await uploadFileWithUploadSession({ file, tags });

    const uploadParams: WhatsAppMediaUploadBodyParams = {
      fileId: finalizedFile.id,
      fromPhoneNumber,
    };

    return await MessagingWhatsappMessagesService.uploadMedia(uploadParams);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: uploadFile,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['files'],
      });

      onSuccess(data);
    },
    onError: (error: Error) => onError(error),
  });

  return {
    uploadFile: mutateAsync,
    loading,
    error,
  };
};
